import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Users, Lightbulb, Award, CheckCircle, Quote, Linkedin } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We hold ourselves to the highest standards in everything we do, delivering work we\'re proud of.',
      color: 'from-brand-purple to-brand-purple-mid',
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'We work alongside our clients as true partners, invested in their success as if it were our own.',
      color: 'from-brand-gold to-brand-gold-lite',
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We continuously evolve our methodologies to stay ahead of change and bring fresh perspectives.',
      color: 'from-brand-purple-lite to-brand-purple',
    },
    {
      icon: Target,
      title: 'Integrity',
      description: 'We operate with transparency, honesty, and ethical principles in every interaction.',
      color: 'from-brand-gold to-brand-gold-lite',
    },
  ];

  const team = [
    {
      name: 'Nilam Patel',
      role: 'Founder & CEO',
      bio: 'With deep expertise in corporate strategy and executive development, Nilam founded Expertize Edge to bring transformational thinking to modern leadership. Her vision drives every engagement and her passion for developing leaders has impacted organizations across industries.',
      image: 'https://customer-assets.emergentagent.com/job_hardcore-galileo-2/artifacts/93fhsanc_Screenshot%202026-04-18%20at%209.23.09%E2%80%AFPM-ezremove.png',
      linkedin: '#',
    },
  ];

  const milestones = [
    { year: '2010', event: 'Founded with a vision to transform leadership development' },
    { year: '2014', event: 'Expanded to serve clients across Asia-Pacific' },
    { year: '2018', event: 'Launched Luminarc™ flagship experience' },
    { year: '2022', event: 'Reached 500+ leaders transformed milestone' },
    { year: '2024', event: 'Introduced AI-age leadership methodologies' },
  ];

  return (
    <main className="pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-brand-charcoal overflow-hidden" data-testid="about-hero">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(92,45,145,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(196,158,77,0.2),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6">
                <Users className="w-4 h-4 text-brand-gold" />
                <span className="text-brand-gold text-sm font-medium">About Expertize Edge</span>
              </span>
              
              <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white leading-tight mb-6">
                Shaping Leaders,{' '}
                <span className="text-brand-gold">Transforming</span>{' '}
                Organizations
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                For over a decade, we've partnered with visionary organizations to build 
                leadership capabilities that drive lasting competitive advantage. We believe 
                in transformation, not just training.
              </p>
              
              <div className="flex flex-wrap gap-6">
                {[
                  { value: '15+', label: 'Years Experience' },
                  { value: '500+', label: 'Leaders Transformed' },
                  { value: '200+', label: 'Organizations' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-3xl font-bold text-brand-gold">{stat.value}</p>
                    <p className="text-gray-400 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=500&fit=crop"
                alt="Team collaboration"
                className="relative rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-brand-bg-light relative overflow-hidden" data-testid="our-story">
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-brand-lavender/50 to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
                Our Story
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-6">
                Born from Purpose,{' '}
                <span className="text-brand-purple">Driven by Impact</span>
              </h2>
              <div className="space-y-4 text-brand-body text-lg leading-relaxed">
                <p>
                  Expertize Edge was founded on a simple belief: that every organization has 
                  untapped potential waiting to be unlocked. What began as a boutique consulting 
                  practice has grown into a comprehensive professional services firm serving 
                  clients across industries and continents.
                </p>
                <p>
                  Our journey has been defined by the transformations we've helped create—executives 
                  who've found their authentic leadership voice, teams that have discovered new ways 
                  of working together, and organizations that have reimagined what's possible.
                </p>
                <p>
                  Today, we continue to push the boundaries of what leadership development and 
                  strategic consulting can achieve, always guided by our commitment to meaningful, 
                  measurable impact.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              {/* Timeline */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-brand-lavender">
                <h3 className="font-heading text-xl font-bold text-brand-charcoal mb-6">Our Journey</h3>
                <div className="space-y-6">
                  {milestones.map((milestone, index) => (
                    <motion.div
                      key={milestone.year}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex gap-4"
                    >
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-brand-purple rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {milestone.year}
                        </div>
                        {index < milestones.length - 1 && (
                          <div className="w-0.5 h-full bg-brand-lavender mt-2" />
                        )}
                      </div>
                      <div className="pt-3">
                        <p className="text-brand-body">{milestone.event}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-white" data-testid="our-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block bg-brand-gold/10 text-brand-gold text-sm font-semibold px-4 py-2 rounded-full mb-4">
              Our Values
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-6">
              What <span className="text-brand-purple">Guides</span> Us
            </h2>
            <p className="text-brand-muted text-lg max-w-2xl mx-auto">
              These principles shape every engagement and define how we show up for our clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-brand-bg-light rounded-2xl p-8 h-full border border-transparent hover:border-brand-purple/20 hover:shadow-xl transition-all duration-300">
                  <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-brand-charcoal mb-3">
                    {value.title}
                  </h3>
                  <p className="text-brand-muted leading-relaxed">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-24 bg-brand-charcoal relative overflow-hidden" data-testid="team-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(196,158,77,0.15),transparent_50%)]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-4">
              <Users className="w-4 h-4 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">Leadership</span>
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Meet the <span className="text-brand-gold">Founder</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Driven by a passion for transformational leadership and decades of 
              experience in corporate strategy and executive development.
            </p>
          </motion.div>

          <div className="flex justify-center">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group max-w-sm w-full"
                data-testid={`team-member-${index}`}
              >
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                  <div className="relative overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-heading text-xl font-bold text-white">
                        {member.name}
                      </h3>
                      <p className="text-brand-gold text-sm">{member.role}</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">{member.bio}</p>
                    <a
                      href={member.linkedin}
                      className="inline-flex items-center gap-2 text-brand-gold hover:text-brand-gold-lite transition-colors"
                    >
                      <Linkedin className="w-4 h-4" />
                      <span className="text-sm">Connect on LinkedIn</span>
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 bg-brand-purple relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Quote className="w-16 h-16 text-brand-gold/30 mx-auto mb-8" />
            <p className="text-3xl sm:text-4xl text-white font-light italic leading-relaxed mb-8">
              "Our mission is simple: to help leaders and organizations discover 
              what they're truly capable of achieving."
            </p>
            <div>
              <p className="text-brand-gold font-semibold text-lg">Nilam Patel</p>
              <p className="text-white/60">Founder & CEO, Expertize Edge</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-charcoal to-brand-purple relative overflow-hidden" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Let's Create Impact Together
            </h2>
            <p className="text-white/80 text-xl mb-10 max-w-2xl mx-auto">
              Ready to explore how we can support your organization's transformation?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-10 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="about-cta-button"
              >
                Get in Touch
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-10 py-4 hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
